/**
 * The names of Claudia's built-in skills — the one place the string literals live.
 *
 * Split out of `defaults.ts` so a *frontend* surface that attaches a skill by name can
 * import the constant instead of retyping the literal (`@repo/convex/assistant/skills/
 * names`). Only names belong here: `defaults.ts` carries the bodies, and pulling those
 * into the web-app bundle would ship every skill prompt to the browser.
 *
 * Renaming a skill therefore breaks the caller at compile time rather than silently
 * attaching a skill that no longer exists.
 *
 * Names are dashed throughout, and read surface first: the first word is the surface (`chat`,
 * `clarification`, `teams`, `documents`, `element`, `quality-guard`), a framework-specific skill
 * carries the framework key as its second word (`da` for Data & Analytics), and an anchored
 * framework skill carries a two-digit order number after it so the alphabetical sort is the
 * composed-prompt order. A `segment:segment` spelling is not a legal name at all
 * (`SKILL_NAME_PATTERN` in `validation.ts`); any spelling persisted rows may still carry is mapped
 * onto its current name by the alias tables in `validation.ts`, which is also where the reserved
 * slot's name is in scope, and each mapping is applied to the data by a migration.
 *
 * This module stays a leaf on purpose — no imports at all — so a hot-path entry module or a
 * browser bundle can pull in a skill name without dragging anything behind it.
 */

/* Anchored Data & Analytics framework skills. The two-digit number is the composition order and
 * the budget priority (higher numbers drop first), so inserting one means renumbering the tail. */
export const CLAUDIA_SKILL_CHAT_DA_PURPOSE = "chat-da-01-purpose";
export const CLAUDIA_SKILL_CHAT_DA_BUSINESS_SCOPE = "chat-da-02-business-scope";
export const CLAUDIA_SKILL_CHAT_DA_SOLUTION_DESIGN = "chat-da-03-solution-design";
export const CLAUDIA_SKILL_CHAT_DA_DATASETS = "chat-da-04-datasets";
export const CLAUDIA_SKILL_CHAT_DA_LOGIC_AND_SERVING = "chat-da-05-logic-and-serving";
export const CLAUDIA_SKILL_CHAT_DA_DELIVERABLES = "chat-da-06-deliverables";
export const CLAUDIA_SKILL_CHAT_DA_CONTENT_BOUNDARIES = "chat-da-07-content-boundaries";
export const CLAUDIA_SKILL_CHAT_DA_ATTRIBUTE_DECISIONS = "chat-da-08-attribute-decisions";
export const CLAUDIA_SKILL_CHAT_DA_CREATING_VERSUS_OFFERING = "chat-da-09-creating-versus-offering";
export const CLAUDIA_SKILL_CHAT_DA_RESPONSE_FORMAT = "chat-da-10-response-format";

/** The anchored block, in composition order — one list so the seed, the anchors and the tests
 * cannot disagree about which skills are anchored for the Data & Analytics framework. The first
 * six describe the framework (purpose, then each side, then the data path element by element);
 * the last four are the cross-cutting authoring rules. */
export const CLAUDIA_ANCHORED_CHAT_DA_SKILL_NAMES = [
  CLAUDIA_SKILL_CHAT_DA_PURPOSE,
  CLAUDIA_SKILL_CHAT_DA_BUSINESS_SCOPE,
  CLAUDIA_SKILL_CHAT_DA_SOLUTION_DESIGN,
  CLAUDIA_SKILL_CHAT_DA_DATASETS,
  CLAUDIA_SKILL_CHAT_DA_LOGIC_AND_SERVING,
  CLAUDIA_SKILL_CHAT_DA_DELIVERABLES,
  CLAUDIA_SKILL_CHAT_DA_CONTENT_BOUNDARIES,
  CLAUDIA_SKILL_CHAT_DA_ATTRIBUTE_DECISIONS,
  CLAUDIA_SKILL_CHAT_DA_CREATING_VERSUS_OFFERING,
  CLAUDIA_SKILL_CHAT_DA_RESPONSE_FORMAT,
] as const;

/* On-demand skills: no order number, so a number in the name is itself the signal that a skill is
 * anchored, readable at a glance in the admin selector. */
export const CLAUDIA_SKILL_CHAT_DA_TECHNICAL_MAP = "chat-da-technical-map";
export const CLAUDIA_SKILL_ELEMENT_EDITING = "element-editing";
export const CLAUDIA_SKILL_ELEMENT_VERSIONS_HISTORY = "element-versions-history";
export const CLAUDIA_SKILL_QUALITY_GUARD_REMEDIATION = "quality-guard-remediation";
export const CLAUDIA_SKILL_CLARIFICATION_CONSULTATION = "clarification-consultation";
export const CLAUDIA_SKILL_CLARIFICATION_TRIAGE = "clarification-triage";
export const CLAUDIA_SKILL_TEAMS_PERSONAL_ASSISTANT = "teams-personal-assistant";
export const CLAUDIA_SKILL_TEAMS_PROJECT_PULSE = "teams-project-pulse";
export const CLAUDIA_SKILL_DOCUMENTS_ANALYZE = "documents-analyze";
export const CLAUDIA_SKILL_DOCUMENTS_HANDOFF = "documents-handoff";

/**
 * The retired built-ins: `project-overview` and `project-guided-discovery` because the model
 * reaches the same result without them, `claudia-conversation` because it carries nothing the
 * universal prompt does not already say.
 *
 * They are names rather than skills: nothing attaches them, and
 * `migrations/20260830_retireRemovedClaudiaSkills` soft-deletes the chains and anchors that still
 * carry them. Kept here, not inlined into that migration, so the retirement list and the catalog
 * are read from one file.
 */
export const CLAUDIA_RETIRED_SKILL_NAMES = [
  "project-guided-discovery",
  "project-overview",
  "claudia-conversation",
] as const;

/** The anchor surface the numbered Data & Analytics chat skills are written for. */
export const CLAUDIA_CHAT_DA_ANCHOR_SURFACE = "interactive-chat" as const;
